import { ChangeEvent } from 'react'
import useProductVariant from '../helpers/useProductVariant'
import { hasOwnProperties } from '../helpers/utils'
import { typeVariant } from '../types'
import { type_WISHLIST_DASHBOARD_STATE } from '../types'

const colourKeys = ['Colours', 'Colors']

type props = {
  variants: Array<typeVariant>
  options: any[]
  updateCurrentVariant: any
  currentVariant: any
  displayType?: string
  hasOnlyDefaultVariant?: boolean
  hasPair?: boolean
}
declare var wishlistDashboardState: type_WISHLIST_DASHBOARD_STATE

function ProductVariant({ variants, options, updateCurrentVariant, currentVariant, displayType, hasOnlyDefaultVariant = false, hasPair = false }: props) {
  const isOnlyVariantDefault = hasOnlyDefaultVariant || (variants.length === 1 && variants[0].title === 'Default Title')

  if (isOnlyVariantDefault) {
    return <></>
  }

  if (variants.length === 1 && hasPair) {
    let temp = { ...variants[0] }
    temp.option2 = 'Matched Pair'
    temp.options = [temp.option1, temp.option2]
    variants.push(temp)
  }

  const { availableValues, selectableValues, selected, setSelected } = useProductVariant({ variants, options, updateCurrentVariant, currentVariant })

  if (hasPair) {
    availableValues['Style'].reverse()
  }

  return (
    <div className="flex flex-col">
      {Object.entries(availableValues).map(([key, values], index) => (
        <VariantGroup
          key={index}
          name={key}
          values={values}
          selected={selected}
          selectableValues={selectableValues}
          setSelected={setSelected}
          displayType={displayType}
        />
      ))}
    </div>
  )
}

type groupProps = {
  name: string
  values: unknown[]
  selected: any
  selectableValues: any
  setSelected: any
  displayType?: string
}
function VariantGroup({ name, values, selected, selectableValues, setSelected, displayType }: groupProps) {
  return (
    values.length > 1 && (
      <div className={`${colourKeys.includes(name) ? 'order-first' : ''} ${name.toLowerCase() === 'letter' ? 'block pt-[45px] lg-max:pt-10' : 'flex pt-4 first:pt-0'}`}>
        {displayType === 'dropdown' ? (
          <VariantDropdown items={values} option={name} selected={selected} setSelected={setSelected} />
        ) : (
          <>
            <div className="flex items-center mr-5 text-sm-b text-ink uppercase font-calibre">
              <span>
                {name.toLowerCase() === 'length'
                  ? wishlistDashboardState.product.size
                  : name.toLowerCase() === 'letter'
                  ? `${wishlistDashboardState.product.choose} ${name}:`
                  : `${name}:`}
              </span>
            </div>
            <VariantList items={values} option={name} selected={selected} selectableValues={selectableValues} setSelected={setSelected} />
          </>
        )}
      </div>
    )
  )
}

type itemProps = {
  className: string
  value: string
  option: string
  selected: any
  selectableValues: any
  setSelected: any
}

function VariantItem({ className, value, option, selected, selectableValues, setSelected }: itemProps) {
  const onSelect = (value: string, option: string) => {
    selected = { ...selected, [option]: value }
    setSelected(selected)
  }

  const isSelected = hasOwnProperties(option, selected) && selected[option] === value

  const isDisabled = hasOwnProperties(option, selectableValues) && !selectableValues[option].includes(value)

  const isColour = colourKeys.includes(option)

  const getSwatchClass = (name: string) => {
    return `colour-swatch-${name.toLowerCase()}`
  }

  return !isColour ? (
    <button
      type="button"
      className={`variant-item flex items-center justify-center uppercase font-calibre text-center text-sm-b text-ink ${className}`}
      name={value}
      onClick={() => onSelect(value, option)}
    >
      <span className={`w-min border-0 border-b border-solid ${isSelected ? ' border-ink' : 'border-transparent'}`}>{value.replace('Letter ', '')}</span>
    </button>
  ) : (
    <button
      type="button"
      className={`variant-item relative mt-2 h-12 w-12 rounded-full border-[3px] border-white outline outline-1 outline-transparent ${getSwatchClass(value)} ${
        isSelected && 'outline-default'
      } ${isDisabled && 'before:absolute before:left-0 before:h-[1px] before:w-full before:rotate-[135deg] before:bg-white before:content-[""]'}`}
      name={value}
      onClick={() => onSelect(value, option)}
      aria-label={getSwatchClass(value)}
    ></button>
  )
}

type listProps = {
  items: any[]
  option: string
  selected: any
  selectableValues: any
  setSelected: any
}

function VariantList({ items, option, selected, selectableValues, setSelected }: listProps) {
  return (
    <div className={`flex w-full flex-wrap ${option.toLowerCase() === 'letter' ? 'gap-x-[5.8px] gap-y-2.5 mt-[15px] -ml-2.5 lg:mt-5 lg-max:gap-x-2.5 lg-max:gap-y-[15px]' : ''}`}>
      {items.length &&
        items.map((item) => (
          <VariantItem
            key={`${option}-${item}`}
            className={option.toLowerCase() === 'letter' ? 'w-[30px] h-[30px]' : 'mr-5 lg-max:mr-[15px]'}
            value={item}
            option={option}
            selected={selected}
            selectableValues={selectableValues}
            setSelected={setSelected}
          />
        ))}
    </div>
  )
}

type dropdownProps = {
  items: any[]
  option: string
  selected: any
  setSelected: any
}

function VariantDropdown({ items, option, selected, setSelected }: dropdownProps) {
  const onChange = (e: ChangeEvent<HTMLSelectElement>) => {
    selected = { ...selected, [option]: e.target.value }
    setSelected(selected)
  }

  return (
    items.length && (
      <select onChange={onChange} className="input !py-2.5 !text-deep-grey">
        {items.map((item) => (
          <option value={item} selected={selected[option] === item}>
            {item}
          </option>
        ))}
      </select>
    )
  )
}

export default ProductVariant
